<script lang="ts">
  import LayoutGrid, { Cell } from '@smui/layout-grid';
  import { onDestroy, onMount } from 'svelte';
  import { getAppConfig } from '../../../common/config/app-config';
  import { isMobileWidth } from '../../../common/util';
  import { getBlogDataByPagination } from '../services/blog-data-services';
  import BlogCard from './BlogCard.svelte';
  import BlogCommonContent from './BlogCommonContent.svelte';
  import BlogVariant1 from './BlogVariant1.svelte';
  import BlogVariant2 from './BlogVariant2.svelte';

  import ExtraAmplienceBannerComponent from '../../common/lazy-components/ExtraAmplienceBannerComponent.svelte';
  import BlogCardSkeletonLoader from './BlogCardSkeletonLoader.svelte';

  const { blogTotalResults } = getAppConfig();

  export let blogDataByDesignType;
  export let blogBanner;
  let bannerComp;
  let page = 1;
  const isResponsive = isMobileWidth();
  $: if (bannerComp) {
    bannerComp.onLoad();
  }
  let scrollComponent;
  let blogCardsLoaded: boolean = false;
  onMount(() => {
    if (blogDataByDesignType?.['default']?.length) {
      observer.observe(scrollComponent);
    }
  });

  onDestroy(() => {
    observer.disconnect();
  });

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const remainingBlogDataCount =
          blogTotalResults -
          (blogDataByDesignType['header-post']?.length ?? 0) -
          (blogDataByDesignType['feature-post-1']?.length ?? 0) -
          (blogDataByDesignType['feature-post-2']?.length ?? 0);
        if (blogDataByDesignType['default'].length < remainingBlogDataCount) {
          fetchNextBlogData('all', page++);
        }
        observer.disconnect();
      }
    });
  });
  /**
   * Fetches the next set of blog posts
   * @param {string} category - The category of blog posts to fetch
   * @param {number} page - The page number of the blog posts to fetch
   * @returns {Promise<{results: BlogPost[]}>} - A promise resolving to an object
   *   containing the next set of blog posts
   */
  async function fetchNextBlogData(category = 'all', page = 1) {
    const response = await getBlogDataByPagination(category, page);
    let nextPostDefaultData = response && response.results && response.results.length ? response.results : [];
    if (nextPostDefaultData.length > 0) {
      // Append the new data to the existing blog posts
      blogDataByDesignType['default'] = [...blogDataByDesignType['default'], ...nextPostDefaultData];
      // If there's a scroll component, observe it for further scroll events
      observer.observe(scrollComponent);
    }
    // Flag that the data has been loaded
    blogCardsLoaded = true;
  }
</script>

<section class="blog-landing-page-section">
  {#if blogDataByDesignType && 'header-post' in blogDataByDesignType && blogDataByDesignType['header-post'] && blogDataByDesignType['header-post'].length}
    <BlogCommonContent addContentClass={isResponsive ? false : true}>
      <section class="">
        {#each blogDataByDesignType['header-post'] as headerPost}
          <BlogVariant1 {headerPost} />
        {/each}
      </section>
    </BlogCommonContent>
  {/if}
  {#if blogDataByDesignType && 'feature-post-1' in blogDataByDesignType && blogDataByDesignType['feature-post-1'] && blogDataByDesignType['feature-post-1'].length}
    <BlogCommonContent addContentClass={isResponsive ? false : true}>
      <section class="blog-feature-post-section">
        {#each blogDataByDesignType['feature-post-1'] as featurePost}
          <BlogVariant2 {featurePost} imageOrder={0} />
        {/each}
      </section>
    </BlogCommonContent>
  {/if}
  {#if blogDataByDesignType && 'feature-post-2' in blogDataByDesignType && blogDataByDesignType['feature-post-2'] && blogDataByDesignType['feature-post-2'].length}
    <BlogCommonContent addContentClass={isResponsive ? false : true}>
      <section class="blog-feature-post-section">
        {#each blogDataByDesignType['feature-post-2'] as featurePost}
          <BlogVariant2 {featurePost} imageOrder={1} />
        {/each}
      </section>
    </BlogCommonContent>
  {/if}
  {#if blogBanner}
    <BlogCommonContent>
      <ExtraAmplienceBannerComponent metaData={blogBanner} bind:this={bannerComp} />
    </BlogCommonContent>
  {/if}
  {#if blogDataByDesignType && 'default' in blogDataByDesignType && blogDataByDesignType['default'] && blogDataByDesignType['default'].length}
    <BlogCommonContent>
      {#if !blogCardsLoaded}
        <BlogCardSkeletonLoader />
      {:else}
        <section class="blog-card-section">
          <LayoutGrid>
            {#each blogDataByDesignType['default'] as blogCard}
              <Cell class="" spanDevices={{ phone: 2, tablet: 2, desktop: 4 }}>
                <BlogCard data={blogCard} />
              </Cell>
            {/each}
          </LayoutGrid>
        </section>
      {/if}
      <div bind:this={scrollComponent} style="width:0px" />
    </BlogCommonContent>
  {/if}
</section>

<style lang="scss">
  @import '../../common/styles/colors.new.scss';

  .blog-landing-page-section {
    background: $primary-white;
    display: flex;
    flex-direction: column;
    gap: 60px;
    @include desktop-max-width {
      gap: 30px;
    }
    .blog-card-section {
      display: flex;
      gap: 60px;
      flex-wrap: wrap;
      padding: 20px 0;
      @include desktop-max-width {
        gap: 15px;
        padding: 30px 0;
      }
    }
    .blog-feature-post-section {
      display: flex;
      flex-direction: column;
      gap: 60px;
    }
  }
  :global {
    .mdc-layout-grid {
      padding: 0 24px;
      @include desktop-max-width {
        padding: 0 12px;
      }
    }
    .mdc-layout-grid__inner {
      gap: 60px;
      @include desktop-max-width {
        gap: 16px;
      }
    }
  }
</style>
